import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Modal, Pagination, Popconfirm, Switch, Tag, Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import SubscriptionCreationModal from '@/components/EntityCreationModals/SubscriptionCreationModal/SubscriptionCreationModal'
import { ROLES_IN_RUSSIAN } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  NotificationPermissionsDto,
  NotificationSubscriptionDto,
  NotificationTypeDto,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import EditSubscriptionModal from './EditSubscriptionModal/EditSubscriptionModal'
import cn from './Notifications.module.scss'
import SubscribersModal from './SubscribersModal/SubscribersModal'

const PAGE_SIZE = 20

function Notifications() {
  const { currentUser, selectedOrganisation } = useUserInfoStore(
    (state) => state
  )
  const orgSelected = Object.keys(selectedOrganisation).length > 0
  const [modal, contextHolder] = Modal.useModal()

  const [permissions, setPermissions] =
    useState<NotificationPermissionsDto | null>(null)
  const [types, setTypes] = useState<NotificationTypeDto[]>([])
  const [subscriptions, setSubscriptions] = useState<
    NotificationSubscriptionDto[]
  >([])
  const [totalElements, setTotalElements] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [editSubscription, setEditSubscription] =
    useState<NotificationSubscriptionDto | null>(null)
  const [subscribersEventType, setSubscribersEventType] = useState<
    string | null
  >(null)

  const getEventType = (eventType: string) =>
    types.find((type) => type.code === eventType)

  const getEventLabel = (eventType: string) =>
    getEventType(eventType)?.description ?? eventType

  useEffect(() => {
    Promise.all([
      api.get('/notifications/permissions'),
      api.get('/notifications/types'),
    ])
      .then(([permRes, typesRes]) => {
        setPermissions(permRes.data)
        setTypes(typesRes.data.types ?? [])
      })
      .catch((e) => handleApiError(e, modal))
  }, [])

  const fetchSubscriptions = (p = 0) => {
    setLoading(true)
    api
      .get(`/notifications/subscriptions?page=${p}&pageSize=${PAGE_SIZE}`)
      .then((r) => {
        const data = r.data.subscriptions
        setSubscriptions(data.content ?? [])
        setTotalElements(data.totalElements ?? 0)
      })
      .catch((e) => handleApiError(e, modal))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSubscriptions(page)
  }, [page])

  const handleToggleStatus = (id: string, isActive: boolean) => {
    api
      .patch(`/notifications/subscriptions/${id}/status`, { isActive })
      .then((r) => {
        setSubscriptions((prev) =>
          prev.map((s) => (s.id === id ? r.data.subscription : s))
        )
      })
      .catch((e) => handleApiError(e, modal))
  }

  const handleDelete = (id: string) => {
    api
      .delete(`/notifications/subscriptions/${id}`)
      .then(() => {
        toast.success('Подписка удалена')
        fetchSubscriptions(page)
      })
      .catch((e) => handleApiError(e, modal))
  }

  const handleCreated = (sub: NotificationSubscriptionDto) => {
    setSubscriptions((prev) => [sub, ...prev])
    setTotalElements((t) => t + 1)
  }

  const handleUpdated = (sub: NotificationSubscriptionDto) => {
    setSubscriptions((prev) => prev.map((s) => (s.id === sub.id ? sub : s)))
  }

  const canEditSubscription = (sub: NotificationSubscriptionDto) =>
    !!getEventType(sub.eventType)?.supportsClientFilters

  const renderTarget = (sub: NotificationSubscriptionDto) => {
    if (sub.targetType === 'USER' && sub.targetUser) {
      return (
        <div className={cn.targetCell}>
          <span className={cn.targetName}>{sub.targetUser.description}</span>
          <span className={cn.targetSub}>{sub.targetUser.login}</span>
        </div>
      )
    }
    if (sub.targetType === 'ROLE_GROUP' && sub.targetRole) {
      return (
        <Tag color="blue" className={cn.tag}>
          Группа: {ROLES_IN_RUSSIAN[sub.targetRole] ?? sub.targetRole}
        </Tag>
      )
    }
    if (sub.targetType === 'CHAT_ID' && sub.targetChatId != null) {
      return (
        <Tag color="purple" className={cn.tag}>
          Чат ID: {sub.targetChatId}
        </Tag>
      )
    }
    return '—'
  }

  const renderFilters = (sub: NotificationSubscriptionDto) => {
    const eventType = getEventType(sub.eventType)
    const filters = sub.filters

    if (!eventType?.supportsClientFilters) {
      return <span className={cn.unavailableFilters}>Недоступны</span>
    }

    if (filters?.allMyClients) {
      return (
        <Tag color="green" className={cn.tag}>
          Все доступные клиенты
        </Tag>
      )
    }

    if (filters?.clients && filters.clients.length > 0) {
      return (
        <Tooltip
          title={filters.clients.map((c) => (
            <div key={c.id} className={cn.clientTooltipRow}>
              <span
                className={`${cn.clientTypeDot} ${c.isLegal ? cn.clientTypeDotLegal : cn.clientTypeDotPhysical}`}
              />
              {c.description}
            </div>
          ))}
        >
          <Tag color="orange" className={cn.tag}>
            Клиентов: {filters.clients.length}
          </Tag>
        </Tooltip>
      )
    }

    return <span className={cn.noDesc}>Не установлены</span>
  }

  return (
    <section
      className={[cn.page, orgSelected ? cn.pageOrgSelected : ''].join(' ')}
    >
      {contextHolder}
      <Loader loaderType="modal" isModalOpen={!permissions || loading} />
      <div className={cn.header}>
        <h1 className={cn.title}>Настройки подписок (Telegram)</h1>

        {currentUser && !currentUser.telegramActivated && (
          <div className={cn.telegramAlert}>
            <span>
              Telegram не подключён. Уведомления не будут доставляться.
            </span>
            <button
              className={cn.telegramBtn}
              onClick={() => {
                api.post('/telegram/link').then((r) => {
                  window.open(r.data.url, '_blank')
                })
              }}
            >
              Подключить Telegram
            </button>
          </div>
        )}
      </div>

      <div className={cn.tableWrap}>
        <div className={cn.tableHeader}>
          <span className={cn.totalLabel}>Подписок: {totalElements}</span>
          <button className={cn.createBtn} onClick={() => setCreateOpen(true)}>
            <PlusOutlined /> Создать подписку
          </button>
        </div>

        {loading ? null : subscriptions.length === 0 ? (
          <div className={cn.empty}>Подписок нет</div>
        ) : (
          <>
            <div className={cn.tableContainer}>
              <table
                className={[
                  cn.table,
                  orgSelected ? cn.tableOrgSelected : '',
                ].join(' ')}
              >
                <thead>
                  <tr>
                    <th>Событие</th>
                    <th>Адресат</th>
                    <th>Фильтры</th>
                    <th>Описание</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td data-label="Событие">
                        <div className={cn.eventCell}>
                          <span>{getEventLabel(sub.eventType)}</span>
                          {permissions?.canManageGlobal && (
                            <Tooltip title="Посмотреть подписчиков">
                              <button
                                className={cn.subscribersBtn}
                                onClick={() =>
                                  setSubscribersEventType(sub.eventType)
                                }
                              >
                                <TeamOutlined />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      <td data-label="Адресат">{renderTarget(sub)}</td>
                      <td data-label="Фильтры">{renderFilters(sub)}</td>
                      <td data-label="Описание" className={cn.descCell}>
                        {sub.description || (
                          <span className={cn.noDesc}>—</span>
                        )}
                      </td>
                      <td data-label="Статус">
                        <Switch
                          checked={sub.isActive}
                          onChange={(val) => handleToggleStatus(sub.id, val)}
                          size="small"
                        />
                      </td>
                      <td data-label="Действия">
                        <div className={cn.actionBtns}>
                          {canEditSubscription(sub) && (
                            <Tooltip title="Редактировать">
                              <button
                                className={cn.editBtn}
                                onClick={() => setEditSubscription(sub)}
                              >
                                <EditOutlined />
                              </button>
                            </Tooltip>
                          )}
                          <Popconfirm
                            title="Удалить подписку?"
                            description="Это действие нельзя отменить"
                            okText="Да"
                            cancelText="Нет"
                            okButtonProps={{
                              className: orgSelected
                                ? cn.confirmOkButtonSelected
                                : cn.confirmOkButton,
                            }}
                            onConfirm={() => handleDelete(sub.id)}
                          >
                            <div className={cn.deleteIconContainer}>
                              <DeleteOutlined title="Удалить" />
                            </div>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalElements > PAGE_SIZE && (
              <div className={cn.pagination}>
                <Pagination
                  current={page + 1}
                  pageSize={PAGE_SIZE}
                  total={totalElements}
                  onChange={(p) => setPage(p - 1)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>

      {createOpen && permissions && (
        <SubscriptionCreationModal
          open={createOpen}
          setOpen={setCreateOpen}
          onCreated={handleCreated}
          permissions={permissions}
          types={types}
          isOrganisationSelected={orgSelected}
        />
      )}

      {permissions && (
        <EditSubscriptionModal
          subscription={editSubscription}
          permissions={permissions}
          eventLabel={
            editSubscription
              ? getEventLabel(editSubscription.eventType)
              : undefined
          }
          onClose={() => setEditSubscription(null)}
          onUpdated={handleUpdated}
        />
      )}

      <SubscribersModal
        eventType={subscribersEventType}
        eventLabel={
          subscribersEventType ? getEventLabel(subscribersEventType) : undefined
        }
        onClose={() => setSubscribersEventType(null)}
      />
    </section>
  )
}

export default Notifications
